import React, { useId } from 'react';
import { Info } from 'lucide-react';
import { PERIOD_GRADES, GROUP_TARGETS, durationText } from '../utils/tradePeriods';
import { ThresholdSelect } from './FilterControls';

/**
 * All Transactions → Analysis switch (Apal 2026-10-17). The trade sheet stays
 * as it is; with Analysis on, it reads as periods — a strong band at the start
 * of each, so scrolling you feel which stretches were Exceptional, Good,
 * Average or Poor, and for how long. Grading lives in utils/tradePeriods.js.
 */
// Greens are quiet, warm colours loud — so scrolling, the eye lands on where
// money slowed (amber) or was lost (red) (Apal 2026-10-17: "intuitive for
// analysts to spot areas when we will be making less money or losing").
export const PERIOD_TONE = {
  exceptional: { edge: 'border-l-emerald-500', bg: 'bg-emerald-500/[0.07]', row: '', chip: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/50' },
  good: { edge: 'border-l-green-300/70', bg: 'bg-green-300/[0.04]', row: '', chip: 'bg-green-300/10 text-green-200 border-green-300/30' },
  average: { edge: 'border-l-amber-400', bg: 'bg-amber-400/[0.12]', row: 'bg-amber-400/[0.05]', chip: 'bg-amber-400/25 text-amber-100 border-amber-400/60' },
  poor: { edge: 'border-l-red-500', bg: 'bg-red-500/[0.15]', row: 'bg-red-500/[0.07]', chip: 'bg-red-500/25 text-red-100 border-red-500/60' },
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthYear = (iso) => `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
const pct = (v) => `${v > 0 ? '+' : ''}${v.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

// What each rating means, in this index's own terms (bar = what holding it
// gives a year) — or, grouped by a target, in the target's terms
export const gradeMeaning = (k, bar, target = null) => (target != null ? {
  exceptional: `Earned ${Math.round(2 * target)}%+ a year — twice the ${target}% target`,
  good: `Earned ${target}%+ a year — met the target`,
  average: `Made money, but under ${target}% a year`,
  poor: 'Lost money',
} : {
  exceptional: `Earned ${Math.round(2 * bar)}%+ a year — twice what this index gives`,
  good: `Earned ${Math.round(bar)}%+ a year — what this index gives — or made visible money and beat it`,
  average: `Made a little: under ${Math.round(bar)}% a year and not beating the index`,
  poor: 'Lost money',
})[k];

/**
 * "Group by" (Apal 2026-10-22): what the periods are rated against — the
 * index's own pace (default) or a target a year. Shown beside the Analysis
 * switch, only while it is on; amber while a target is set.
 */
export const GroupBySelect = ({ id, value, onChange, testid = 'group-by' }) => (
  <ThresholdSelect inline id={id} label="Group by" testid={testid} selectTestid={`${testid}-select`}
    value={value == null ? 'index' : String(value)} active={value != null}
    onChange={v => onChange(v === 'index' ? null : Number(v))}
    title="What the periods are rated against: what simply holding the index gives, or a return you set a year"
    options={[{ value: 'index', label: 'Index pace' }, ...GROUP_TARGETS.map(v => ({ value: String(v), label: `${v}% a year` }))]} />
);

/** The band at the start of a period: only what decides its grade. */
export const PeriodHeaderRow = ({ period: p, colSpan, bar, target = null }) => {
  const tone = PERIOD_TONE[p.grade];
  return (
    <tr data-testid="period-head" data-grade={p.grade}>
      <td colSpan={colSpan} className={`px-3 py-3 border-t-2 border-dark-600 border-l-4 text-sm ${tone.edge} ${tone.bg}`}>
        <span title={gradeMeaning(p.grade, bar, target)} className={`px-2.5 py-0.5 rounded border text-xs font-bold uppercase tracking-wide mr-3 ${tone.chip}`}>{PERIOD_GRADES[p.grade]}</span>
        <span className="text-white font-semibold">{monthYear(p.from)} – {monthYear(p.to)}</span>
        <span className="text-dark-300"> · {durationText(p.months)}</span>
        <span className="text-dark-300"> · <span className={`font-semibold tabular-nums ${p.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pct(p.net)}</span>
          <span className="text-dark-400 tabular-nums"> ({pct(p.pace).replace('+', '')}/yr)</span></span>
        {p.indexRet != null && (
          <span className="text-dark-400"> · index <span className="text-blue-300 font-semibold tabular-nums">{pct(p.indexRet)}</span>
            {p.beatIndex && <span className="text-green-300"> · beat the index</span>}
            {p.behindIndex && <span className="text-amber-200"> · behind the index</span>}
          </span>
        )}
      </td>
    </tr>
  );
};

/** (i) — how the trades are grouped into periods, in three plain steps. */
// one per card on Signal Analysis — so each tooltip gets its own id
const HowPeriodsAreMade = ({ bar, target }) => {
  const tipId = `periods-howto-tip-${useId().replace(/:/g, '')}`;
  return (
  <span className="relative group inline-flex" data-testid="periods-howto">
    {/* labelled, in the accent colour, so clients see they can open it */}
    <button type="button" aria-describedby={tipId}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-primary-500/40 bg-primary-500/10 text-primary-300 font-semibold hover:bg-primary-500/20 hover:text-primary-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-400 cursor-help">
      <Info className="w-3.5 h-3.5" aria-hidden="true" />
      How periods are made
    </button>
    <span id={tipId} role="tooltip"
      className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity absolute left-0 top-6 z-30 w-[22rem] rounded-lg border border-dark-600 bg-dark-900 px-3.5 py-3 text-xs leading-relaxed text-dark-200 shadow-xl">
      <span className="block font-semibold text-white mb-1.5">How periods are made</span>
      <span className="block"><span className="text-white">1.</span> Trades are grouped into runs: a streak of wins plus the losses right after it.</span>
      {target != null ? (
        <span className="block mt-1"><span className="text-white">2.</span> Each run is rated against your target of {target}% a year: <span className="text-emerald-300">Exceptional</span> {2 * target}%+ a year · <span className="text-green-300">Good</span> met the target · <span className="text-amber-300">Average</span> made money, under the target · <span className="text-red-300">Poor</span> lost money.</span>
      ) : (
        <span className="block mt-1"><span className="text-white">2.</span> Each run is rated against what simply holding this index gives (about {Math.round(bar)}% a year): <span className="text-emerald-300">Exceptional</span> twice that or more · <span className="text-green-300">Good</span> at least that, or beat the index · <span className="text-amber-300">Average</span> made less · <span className="text-red-300">Poor</span> lost money.</span>
      )}
      <span className="block mt-1"><span className="text-white">3.</span> Neighbouring runs with the same rating join into one period. Stretches under 3 months fold into a neighbour, unless they lost real money.</span>
    </span>
  </span>
  );
};

const BAR_FILL = { exceptional: '#10b981', good: '#86efac', average: '#fbbf24', poor: '#ef4444' };

/** Once above the sheet: a barometer of the time spent in each rating, and the longest hard stretch. */
export const PeriodSummary = ({ time, longestHard, bar, target = null }) => {
  const keys = ['exceptional', 'good', 'average', 'poor'];
  const total = keys.reduce((s, k) => s + (time[k] || 0), 0) || 1;
  return (
    <div data-testid="periods-summary" className="mb-4 space-y-2">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-dark-700 gap-px" data-testid="periods-barometer"
        role="img" aria-label={keys.map(k => `${PERIOD_GRADES[k]} ${durationText(time[k] || 0)}`).join(', ')}>
        {keys.filter(k => time[k] > 0).map(k => (
          <div key={k} title={`${PERIOD_GRADES[k]} · ${durationText(time[k])} · ${gradeMeaning(k, bar, target)}`}
            style={{ width: `${(time[k] / total) * 100}%`, background: BAR_FILL[k] }} />
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-xs text-dark-300">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          <HowPeriodsAreMade bar={bar} target={target} />
          {keys.map(k => (
            <span key={k} data-testid={`periods-time-${k}`} title={gradeMeaning(k, bar, target)} className="inline-flex items-center gap-1.5 cursor-help">
              <span className="w-2 h-2 rounded-sm" style={{ background: BAR_FILL[k] }} />
              {PERIOD_GRADES[k]} <span className="text-white font-semibold tabular-nums">{Math.round(((time[k] || 0) / total) * 100)}%</span>
              <span className="text-dark-400 tabular-nums">· {durationText(time[k] || 0)}</span>
            </span>
          ))}
        </div>
        <span data-testid="periods-longest-hard">
          {longestHard ? (
            <>Longest hard stretch <span className={`font-semibold ${longestHard.grade === 'poor' ? 'text-red-300' : 'text-amber-200'}`}>{durationText(longestHard.months)}</span>
              <span className="text-dark-400"> · {monthYear(longestHard.from)} – {monthYear(longestHard.to)} · </span>
              <span className={`tabular-nums ${longestHard.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pct(longestHard.net)}</span>
              {longestHard.indexRet != null && <span className="text-dark-400"> vs index <span className="text-blue-300 tabular-nums">{pct(longestHard.indexRet)}</span></span>}</>
          ) : 'No average or poor stretch'}
        </span>
      </div>
    </div>
  );
};
